import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, LineChart } from "lucide-react";

import { AssetDisplay } from "@/components/ui/asset-display";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { AssetTablePerformance } from "../columns";

interface MobilePositionsListProps {
  data: AssetTablePerformance[];
  currencyCode: string;
  pnlDisplayMode: PnlDisplayMode;
  onViewTrades: (asset: AssetTablePerformance) => void;
}

type PnlDisplayMode = "absolute" | "percentage";

const ABSOLUTE_COLOR_POSITIVE = "#10b981";
const ABSOLUTE_COLOR_NEGATIVE = "#ef4444";

export function MobilePositionsList({
  data,
  currencyCode,
  pnlDisplayMode,
  onViewTrades,
}: MobilePositionsListProps) {
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);

  useEffect(() => {
    if (!expandedAssetId) return;
    const stillExists = data.some((asset) => asset.asset_id === expandedAssetId);
    if (!stillExists) {
      setExpandedAssetId(null);
    }
  }, [data, expandedAssetId]);

  const formatCurrency = useMemo(
    () => (value: number, code: string) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value),
    [],
  );

  const formatPercentage = useMemo(
    () => (value: number) =>
      new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value / 100),
    [],
  );

  const formatShares = useMemo(
    () => (value: number | null | undefined) => {
      if (value === null || value === undefined) return "—";
      return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 4,
      }).format(value);
    },
    [],
  );

  const formatNullableCurrency = (
    value: number | null | undefined,
    code: string,
  ) => {
    if (value === null || value === undefined) return "—";
    return formatCurrency(value, code);
  };

  const renderMetric = (
    label: string,
    value: string,
    colorClass?: string,
    mono = true,
  ) => (
    <div className="space-y-0.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn("text-sm", mono && "font-mono", colorClass)}>{value}</p>
    </div>
  );

  return (
    <div className="divide-y divide-border/20">
      {data.map((asset) => {
        const isExpanded = expandedAssetId === asset.asset_id;
        const isClosed = asset.is_closed === true;
        const pnl = asset.performance_absolute ?? 0;
        const pnlPositive = pnl >= 0;
        const nativeCurrency = asset.asset_currency || "USD";
        const currentValue = asset.current_value ?? 0;
        const returnPct = asset.performance_percentage ?? 0;
        const returnPctPositive = returnPct >= 0;
        const assetPnlNative = asset.performance_absolute_native ?? 0;
        const assetPnlNativePositive = assetPnlNative >= 0;
        const assetReturnNative = asset.performance_percentage_native ?? 0;
        const assetReturnNativePositive = assetReturnNative >= 0;
        const realized = asset.realized_pnl ?? 0;
        const realizedPositive = realized >= 0;
        const unrealized = asset.unrealized_pnl ?? 0;
        const unrealizedPositive = unrealized >= 0;
        const summaryPnlValue =
          pnlDisplayMode === "absolute"
            ? `${pnlPositive ? "+" : ""}${formatCurrency(pnl, currencyCode)}`
            : `${returnPctPositive ? "+" : ""}${formatPercentage(returnPct)}`;

        const dailyPnl = asset.daily_pnl_base;
        const dailyPct = asset.daily_return_pct;
        const quoteDay = asset.asset_quote_daily_change_pct;
        const lastPrice = asset.latest_price;
        const weight = asset.weight_pct;

        return (
          <div key={asset.asset_id} className="px-3 py-3 sm:px-4">
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() =>
                  setExpandedAssetId(isExpanded ? null : asset.asset_id)
                }
                className="min-h-11 w-full min-w-0 flex-1 text-left rounded-lg transition-colors hover:bg-muted/20 p-1 -m-1"
                aria-expanded={isExpanded}
                aria-label={`Toggle details for ${asset.symbol}`}
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                  <div className="min-w-0 overflow-hidden">
                    <Link
                      to="/screener/$assetId"
                      params={{ assetId: asset.asset_id }}
                      className="block min-w-0 overflow-hidden"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="min-w-0 overflow-hidden">
                        <AssetDisplay asset={asset} size="sm" />
                      </div>
                    </Link>
                    {isClosed && (
                      <span className="inline-flex mt-1 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                        CLOSED
                      </span>
                    )}
                  </div>

                  <div className="w-[110px] shrink-0 pl-1 text-right">
                    <p className="font-mono text-sm font-semibold">
                      {formatCurrency(currentValue, currencyCode)}
                    </p>
                    <p
                      className="font-mono text-xs font-medium"
                      style={{
                        color: pnlPositive
                          ? ABSOLUTE_COLOR_POSITIVE
                          : ABSOLUTE_COLOR_NEGATIVE,
                      }}
                    >
                      {summaryPnlValue}
                    </p>
                    {lastPrice !== undefined &&
                      lastPrice !== null &&
                      !isClosed && (
                        <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                          {formatNullableCurrency(
                            lastPrice,
                            nativeCurrency,
                          )}
                        </p>
                      )}
                  </div>
                </div>
              </button>

              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 shrink-0"
                onClick={() =>
                  setExpandedAssetId(isExpanded ? null : asset.asset_id)
                }
                aria-label={`Toggle details for ${asset.symbol}`}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>

            {isExpanded && (
              <div className="mt-3 ml-1 rounded-xl border border-border/40 bg-muted/10 p-3 animate-in fade-in-0 slide-in-from-top-1 duration-200">
                <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                  {dailyPnl != null &&
                    renderMetric(
                      "Daily P&L",
                      `${dailyPnl >= 0 ? "+" : ""}${formatCurrency(
                        dailyPnl,
                        currencyCode,
                      )}`,
                      dailyPnl >= 0 ? "text-emerald-500" : "text-red-500",
                    )}
                  {dailyPct != null &&
                    renderMetric(
                      "Daily %",
                      `${dailyPct >= 0 ? "+" : ""}${formatPercentage(dailyPct)}`,
                      dailyPct >= 0 ? "text-emerald-500" : "text-red-500",
                    )}
                  {quoteDay != null &&
                    renderMetric(
                      "Day (quote)",
                      `${quoteDay >= 0 ? "+" : ""}${formatPercentage(quoteDay)}`,
                      quoteDay >= 0 ? "text-emerald-500" : "text-red-500",
                    )}
                  {weight != null &&
                    renderMetric(
                      "Weight",
                      `${new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      }).format(weight)}%`,
                    )}
                  {renderMetric(
                    "Net Return",
                    `${returnPctPositive ? "+" : ""}${formatPercentage(
                      returnPct,
                    )}`,
                    returnPctPositive ? "text-emerald-500" : "text-red-500",
                  )}
                  {renderMetric("Shares", formatShares(asset.shares_owned))}
                  {renderMetric(
                    "Avg Cost",
                    formatNullableCurrency(
                      asset.avg_cost_basis_native ?? asset.avg_cost_basis,
                      nativeCurrency,
                    ),
                  )}
                  {renderMetric(
                    "Asset P&L",
                    `${assetPnlNativePositive ? "+" : ""}${formatCurrency(
                      assetPnlNative,
                      nativeCurrency,
                    )}`,
                    assetPnlNativePositive ? "text-emerald-500" : "text-red-500",
                  )}
                  {renderMetric(
                    "Asset Return",
                    `${assetReturnNativePositive ? "+" : ""}${formatPercentage(
                      assetReturnNative,
                    )}`,
                    assetReturnNativePositive ? "text-emerald-500" : "text-red-500",
                  )}
                  {renderMetric(
                    "Realized P&L",
                    `${realizedPositive ? "+" : ""}${formatCurrency(
                      realized,
                      currencyCode,
                    )}`,
                    realizedPositive ? "text-emerald-500" : "text-red-500",
                  )}
                  {renderMetric(
                    "Unrealized P&L",
                    `${unrealizedPositive ? "+" : ""}${formatCurrency(
                      unrealized,
                      currencyCode,
                    )}`,
                    unrealizedPositive ? "text-emerald-500" : "text-red-500",
                  )}
                  {renderMetric(
                    "Dividends",
                    formatCurrency(asset.dividends_received ?? 0, currencyCode),
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 h-10 w-full justify-center"
                  onClick={() => onViewTrades(asset)}
                >
                  <LineChart className="h-4 w-4 mr-2" />
                  View trades
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
